import { useRef, useState } from 'react'

import { cloneDeep, sumBy } from 'lodash'
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material'
import { useHistory } from 'react-router'

import Button from '../../../../components/common/buttons/Button'
import useToasterHelper from '../../../../helpers/ToasterHelper'
import { IRegistrationFeesInterface } from '../../../../models/registration-fees/registrationFees.interface'
import { IRegistrationTicketInterface } from '../../../../models/registration-tickets/registrationTicket.interface'
import { IRegistrationByDayInterface } from '../../../../models/registrations-by-day/registrationByDay.interface'
import {
  resetExhibitorRefundFields,
  selectExhibitorRefundErrors,
  selectExhibitorRefundTotals,
  selectExhibitorTotals,
} from '../../../../store/exhibitor/exhibitorSlice'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import Accordion from '../../../../components/accordion/common/Accordion'
import { httpService } from '../../../../services/httpService'
import { addSalesTax, getCurrency } from '../../../../helpers/payment'
import { convertPrice, getFloatPrice } from '../../../../helpers/price'

import { MESSAGES_CONST } from '../../../../const/messages-const'
import { CONST } from '../../../../const/const'
import { IEventReviewPublish } from '../../../../models/event-review-publish/event-review-publish.interface'
import { IRefundPayload, IRefundPaymentInfo } from '../../../../types/payment'

// Types
type IProps = {
  registrationId: string
  registrationData: IRegistrationByDayInterface[]
  registrationfeesData: IRegistrationFeesInterface[]
  registrationticketData: IRegistrationTicketInterface[]
  setRegistrationData: React.Dispatch<React.SetStateAction<IRegistrationByDayInterface[]>>
  setRegistrationfeesData: React.Dispatch<React.SetStateAction<IRegistrationFeesInterface[]>>
  setRegistrationTicketData: React.Dispatch<React.SetStateAction<IRegistrationTicketInterface[]>>
  eventId: string
  eventData: IEventReviewPublish | null
}

const MINIMUM_AMOUNT_TO_REFUND = 1

const RefundPageFooter = ({
  registrationId,
  registrationData,
  registrationfeesData,
  registrationticketData,
  setRegistrationData,
  setRegistrationTicketData,
  setRegistrationfeesData,
  eventId,
  eventData,
}: IProps) => {
  const history = useHistory()

  // Hooks and vars
  const toastFunction = useToasterHelper()
  const totals = useAppSelector(selectExhibitorTotals)
  const refundTotals = useAppSelector(selectExhibitorRefundTotals)
  const refundErrors = useAppSelector(selectExhibitorRefundErrors)

  const textAreaText = useRef<string>('')

  const dispatch = useAppDispatch()

  let total =
    convertPrice(totals.eventsPriceTotal) +
    convertPrice(totals.feesPriceTotal) +
    convertPrice(totals.ticketsPriceTotal)

  let refundTotal =
    sumBy(
      refundTotals.eventsRefundPriceTotal,
      (item) =>
        convertPrice(item.amountRefunded) +
        addSalesTax(
          item.created,
          convertPrice(item.amountRefunded),
          item.includeSalesTax,
          eventData?.EventPaymentSettings?.chargeSalesTax
        )
    ) +
    sumBy(
      refundTotals.feesRefundPriceTotal,
      (item) =>
        convertPrice(item.amountRefunded) +
        addSalesTax(
          item.created,
          convertPrice(item.amountRefunded),
          item.includeSalesTax,
          eventData?.EventPaymentSettings?.chargeSalesTax
        )
    ) +
    sumBy(
      refundTotals.ticketsRefundPriceTotal,
      (item) =>
        convertPrice(item.amountRefunded) +
        addSalesTax(
          item.created,
          convertPrice(item.amountRefunded),
          item.includeSalesTax,
          eventData?.EventPaymentSettings?.chargeSalesTax
        )
    )

  const onRefundButtonClickFn = async () => {
    if (!eventData?.owner) return

    let error = Object.values(refundErrors).find((currValue) => {
      return !!currValue
    })

    if (total < MINIMUM_AMOUNT_TO_REFUND) {
      error = `The refund amount must be more than ${currency}${MINIMUM_AMOUNT_TO_REFUND}`
    } else if (total < refundTotal) {
      error = `The max amount that can be refunded is ${currency}${total / 100}`
    }

    if (error)
      return toastFunction.error({
        message: error,
      })

    const paymentInfo: IRefundPaymentInfo[] = []

    refundTotals.eventsRefundPriceTotal?.forEach((eventsRefundPriceTotal) => {
      const isExist = paymentInfo.find(
        (payment) => payment.chargeId === eventsRefundPriceTotal.invoiceId
      )
      if (
        eventsRefundPriceTotal.invoiceId &&
        eventsRefundPriceTotal.recipientId &&
        eventsRefundPriceTotal.invoiceUrl &&
        !isExist
      )
        paymentInfo.push({
          chargeId: eventsRefundPriceTotal.invoiceId,
          invoiceUrl: eventsRefundPriceTotal.invoiceUrl,
          recipientId: eventsRefundPriceTotal.recipientId,
        })
    })

    refundTotals.ticketsRefundPriceTotal?.forEach((ticketsRefundPriceTotal) => {
      const isExist = paymentInfo.find(
        (payment) => payment.chargeId === ticketsRefundPriceTotal.invoiceId
      )
      if (
        ticketsRefundPriceTotal.invoiceId &&
        ticketsRefundPriceTotal.recipientId &&
        ticketsRefundPriceTotal.invoiceUrl &&
        !isExist
      )
        paymentInfo.push({
          chargeId: ticketsRefundPriceTotal.invoiceId,
          invoiceUrl: ticketsRefundPriceTotal.invoiceUrl,
          recipientId: ticketsRefundPriceTotal.recipientId,
        })
    })

    refundTotals.feesRefundPriceTotal?.forEach((feesRefundPriceTotal) => {
      const isExist = paymentInfo.find(
        (payment) => payment.chargeId === feesRefundPriceTotal.invoiceId
      )

      if (
        feesRefundPriceTotal.invoiceId &&
        feesRefundPriceTotal.recipientId &&
        feesRefundPriceTotal.invoiceUrl &&
        !isExist
      )
        paymentInfo.push({
          chargeId: feesRefundPriceTotal.invoiceId,
          invoiceUrl: feesRefundPriceTotal.invoiceUrl,
          recipientId: feesRefundPriceTotal.recipientId,
        })
    })

    const refundAmountApiData: IRefundPayload = {
      scratchedFeesItems: [],
      scratchedTicketItems: [],
      scratchedRegistrationByDayItems: [],
      comment: textAreaText.current,
      reason: 'requested_by_customer',
      paymentInfo,
      registrationDocId: registrationId,
      eventOwnerId: eventData?.owner,
    }

    const errors: string[] = []

    const registrationfeesData_ = cloneDeep(registrationfeesData).map((currRegistrationFees) => {
      if (!currRegistrationFees.refundAmountAdded && currRegistrationFees.isScratched) {
        currRegistrationFees.refundAmountAdded = true

        if (!currRegistrationFees.invoiceId && currRegistrationFees.amountRefunded > 0) {
          errors.push(currRegistrationFees.feesTitle || '')
        } else if (currRegistrationFees.invoiceId && currRegistrationFees.amountRefunded > 0) {
          refundAmountApiData.scratchedFeesItems.push({
            docId: currRegistrationFees.id!,
            amountToRefund: currRegistrationFees.amountRefunded,
            salesTax:
              addSalesTax(
                currRegistrationFees.created,
                convertPrice(currRegistrationFees.amountRefunded),
                currRegistrationFees.includeSalesTax,
                eventData?.EventPaymentSettings?.chargeSalesTax
              ) / 100,
            invoiceId: currRegistrationFees.invoiceId,
          })
        }
      }
      return currRegistrationFees
    })

    const registrationData_ = cloneDeep(registrationData).map((currRegistration) => {
      if (!currRegistration.refundAmountAdded && currRegistration.isScratched) {
        currRegistration.refundAmountAdded = true

        if (!currRegistration.invoiceId && currRegistration.amountRefunded > 0) {
          errors.push(currRegistration.registrationByDayName || '')
        } else if (currRegistration.invoiceId && currRegistration.amountRefunded > 0) {
          refundAmountApiData.scratchedRegistrationByDayItems.push({
            docId: currRegistration.id!,
            amountToRefund: currRegistration.amountRefunded,
            salesTax:
              addSalesTax(
                currRegistration.created,
                convertPrice(currRegistration.amountRefunded),
                currRegistration.includeSalesTax,
                eventData?.EventPaymentSettings?.chargeSalesTax
              ) / 100,
            invoiceId: currRegistration.invoiceId,
          })
        }
      }
      return currRegistration
    })

    const registrationticketData_ = cloneDeep(registrationticketData).map(
      (currRegistrationTicket) => {
        if (!currRegistrationTicket.refundAmountAdded && currRegistrationTicket.isScratched) {
          currRegistrationTicket.refundAmountAdded = true
          if (!currRegistrationTicket.invoiceId && currRegistrationTicket.amountRefunded > 0) {
            errors.push(currRegistrationTicket.ticketTitle || '')
          } else if (
            currRegistrationTicket.invoiceId &&
            currRegistrationTicket.amountRefunded > 0
          ) {
            refundAmountApiData.scratchedTicketItems.push({
              docId: currRegistrationTicket.id!,
              amountToRefund: currRegistrationTicket.amountRefunded,
              salesTax:
                addSalesTax(
                  currRegistrationTicket.created,
                  convertPrice(currRegistrationTicket.amountRefunded),
                  currRegistrationTicket.includeSalesTax,
                  eventData?.EventPaymentSettings?.chargeSalesTax
                ) / 100,
              invoiceId: currRegistrationTicket.invoiceId,
            })
          }
        }
        return currRegistrationTicket
      }
    )

    if (errors?.[0]) {
      toastFunction.error({
        message: `Some items are scratched but not paid, please uncheck them: ${errors.join(', ')}`,
      })
      return null
    }

    try {
      setLoading(true)

      await httpService({
        url: 'refund_items_amount',
        data: refundAmountApiData,
        method: 'POST',
      })

      setRegistrationData(registrationData_)
      setRegistrationTicketData(registrationticketData_)
      setRegistrationfeesData(registrationfeesData_)

      dispatch(resetExhibitorRefundFields())

      toastFunction.success({
        message: `Amount refunded successfully`,
      })

      history.push(`${CONST.ROUTES.MANAGE.REVENUE.SUB_URLS.REGISTRATION.URL}/${eventId}`)
    } catch (error: any) {
      console.error(error, 'error')
      toastFunction.error({
        message: error?.response?.data?.devMessage || MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    } finally {
      setLoading(false)
    }
  }

  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const currency = getCurrency(eventData?.EventPaymentSettings?.currency)

  return (
    <>
      <Accordion
        icon={<AddCircleOutline className="add_icon text-[red]" />}
        initialState={true}
        className="mb-2"
        header={
          <div
            className={`text-SeabiscuitDark200ThemeColor flex justify-between items-center ${isOpen ? 'font-normal' : 'font-bold'}`}
          >
            Comments
          </div>
        }
        headerTextClassName="text-SeabiscuitDark200ThemeColor capitalize w-full pr-2"
        onOpen={() => {
          setIsOpen(true)
        }}
        onClose={() => {
          setIsOpen(false)
        }}
        activeIcon={<RemoveCircleOutline className="add_icon text-[red]" />}
      >
        <textarea
          onChange={(e) => (textAreaText.current = e.target.value)}
          rows={6}
          placeholder="Add note to customer...(optional)"
          className="w-full border border-solid !ring-0 !border-[#D3DAEE] rounded-lg font-normal text-[14px] text-SeabiscuitDark200ThemeColor"
        />
      </Accordion>

      <Button
        disabled={refundTotal <= 0}
        fullWidth={true}
        className="mb-2 shadow-none"
        label={`Refund ${getFloatPrice(`${currency}${refundTotal / 100}`)}`}
        loading={loading}
        onClick={onRefundButtonClickFn}
      />
      <Button
        disabled={loading}
        label="Cancel"
        background="muted"
        className="shadow-none"
        fullWidth={true}
        onClick={() =>
          history.push(`${CONST.ROUTES.MANAGE.REVENUE.SUB_URLS.REGISTRATION.URL}/${eventId}`)
        }
      />
    </>
  )
}

export default RefundPageFooter
